import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { catchError, Observable, Subject, takeUntil, tap } from 'rxjs';
// Controllers
import { LoadingController, NavParams, PopoverController, ToastController } from '@ionic/angular';
// Models
import { AutopilotOperation, AutopilotTrajectory, MultipleApiResponse, PlanningItem } from '@mocca-technologies/model';
// Services
import { AutopilotService } from 'src/app/services/ap-operations.service';
import { CoreService } from 'src/app/services/core.service';
// Components
import { DirectoryTreeComponent } from '../directory-tree/directory-tree.component';
// Utils
import { environment } from 'src/environments/environment';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';

@Component({
    selector: 'app-checkbox-tree-general',
    templateUrl: './checkbox-tree-general.component.html',
    styleUrls: ['./checkbox-tree-general.component.scss']
})
export class CheckboxTreeGeneralComponent {

    /**
     * Variables
     */
    apOperations: PlanningItem[] = new Array<AutopilotOperation>() as PlanningItem[];

    selectedOperation: AutopilotOperation = null;

    originalOperation: AutopilotOperation = null;

    private destroy = new Subject<void>();

    /**
     * Child variables
     */
    @ViewChild(DirectoryTreeComponent) directoryTreeComponent: DirectoryTreeComponent;

    constructor(public autopilotService: AutopilotService,
        public navParams: NavParams,
        private popoverController: PopoverController,
        private loadingControl: LoadingController,
        private toastController: ToastController,
        private coreService: CoreService,
        private cdr: ChangeDetectorRef) {
    }

    // #region --> LifeCycle

    async ngOnInit() {

        this.selectedOperation = this.originalOperation;

        this.autopilotService.operationSelected$
            .pipe(
                takeUntil(this.destroy),
                tap((operation) => {
                    this.selectedOperation = operation;
                })
            )
            .subscribe();

        (await this.obtainData()).subscribe();
    }

    ngOnDestroy() {
        this.destroy.next();
    }

    // #endregion --> LifeCycle

    // #region --> Modal Methods

    /**
     * This method is used when cancelling the operation selection. If null operation is selected, the original one is used.
     * @returns
     */
    cancel() {
        this.selectTrajectory([this.originalOperation as PlanningItem]);
        this.autopilotService.setSelectedOperation(this.originalOperation);
        return this.popoverController.dismiss(null, 'cancel');
    }

    /**
     * This method is used when confirming the operation selection. If null operation is selected, the original one is used.
     * @returns
     */
    async confirm() {
        if (!this.selectedOperation) {
            this.selectTrajectory([this.originalOperation as PlanningItem]);

            this.autopilotService.setSelectedOperation(this.originalOperation);

            const toast = await this.toastController.create({
                message: 'Operation selection is mandatory, presaved operation has been automatically chosen.',
                duration: 2500,
                position: 'bottom',
            });

            await toast.present();
        }
        return this.popoverController.dismiss(this.selectedOperation, 'confirm');
    }

    // #endregion --> Modal Methods

    // #region --> Data Loading

    /**
     * This method is used to obtain all the AutopilotOperations
     * @returns
     */
    async obtainData(): Promise<Observable<AutopilotOperation[]>> {
        const loading = await this.loadingControl.create({
            message: 'Loading autopilot operations...',
            spinner: 'bubbles',
        });

        await loading.present();

        return this.autopilotService.apOperations$.pipe(
            tap((apOperations: AutopilotOperation[]) => {
                this.apOperations = apOperations as PlanningItem[];

                this.cdr.detectChanges();

                if(this.selectedOperation) {
                    this.directoryTreeComponent.loadItems([this.selectedOperation as PlanningItem]);
                } else{
                    this.directoryTreeComponent.loadItems();
                }



                loading.dismiss();

                return apOperations;
            }),
            catchError(() => {
                return [];
            })
        );
    }

    // #endregion --> Data Loading

    // #region --> Methods

    /**
     * This method is used when a trajectory is selected in the tree component
     * @param operations
     */
    selectTrajectory(operations: PlanningItem[]) {

        let operation = operations.pop();

        !environment.production && console.log("Selected operation: ", this.selectedOperation)
        !environment.production && console.log("New operation: ", operation)

        if(operation?.uniqueId === this.selectedOperation?.uniqueId) {
            this.autopilotService.setSelectedOperation(null);
        } else{
            this.autopilotService.setSelectedOperation(operation);

            if(operation?.mainTrajectory){

                this.autopilotService
                .getDetailedTrajectory(operation.mainTrajectory.trajectoryUUID)
                .subscribe({
                  next: (res: HttpResponse<MultipleApiResponse>) => {
                    const apTrajectory = res?.body?.responseObject as AutopilotTrajectory;
                    !environment.production && console.log(apTrajectory);
                  },
                  error: (error: HttpErrorResponse) => {
                    this.coreService.showServerResponse(error?.status, error?.error);
                  }
                });
            }
        }

    }

    // #endregion --> Methods
}

